'use strict';

import { account } from "../../fundus/account";
import { ferry } from "../ferry/ferry";
import { sessionState } from "../sessionState/sessionState";
import { upgradePath } from "./subscriptionPrices";
import * as moment from "moment";

export type discount = {
    coupon: {
        percent_off?: number
        amount_off?: number
        duration_in_months?: number
    }
    end?: number
};

type stripeSubscriptionPlan = {
    id: string
    interval: 'month'|'year'
    amount: number
};

export type StripeSubscription = {
    plan: stripeSubscriptionPlan
    trial_end?: number // timestamp
    plan_level?: string
    cancel_at_period_end: boolean
    discount?: discount
    current_period_end?: number // timestamp
    status?: string;
};

export type PaymentSource = {
    id: string;
};

export type StripeCustomer = {
    id: string
    subscriptions: {
        data: Array<StripeSubscription>
    }
    sources: {
        data: Array<PaymentSource>
    }
    default_source: string;
    currency: 'usd'|'eur'|'gbp'
    discount?: discount
    delinquent: boolean
    account_balance?: number
};

type invoiceLine = {
    plan?: {
        id: string
    }
    description?: string
};

type stripeCollection<T> = {
    data: Array<T>
    has_more: boolean;
};

type charge = {
    amount: number
    amount_refunded: number
    created: number
    id: string
    invoice: string // invoice.id
    paid: boolean
    source: card
    metadata: {
        account_balance: number
        credit: number
        description: string
    }
};

type chargeCollection = stripeCollection<charge>;

export type Invoice = {
    lines: {
        data: Array<invoiceLine>
    }
    paid?: boolean
    date: number
    id: string
    charge: string // charge.id
    total: number
    ending_balance: number
};

type invoiceCollection = stripeCollection<Invoice>;

type card = {
    id: string
    last4: string
};

export const minimum_stripe_charge = 50;

export function isTrialPlan(plan: string) {
    return (["trial", "launch"].indexOf(plan.split('|')[1]) > -1) || plan.split('|')[0] === '006';
}

const gem_prefixes = ['GEM', '001', '002', '003', '004', '005'];

function planPrefix(sub: StripeSubscription) {
    return sub.plan ? sub.plan.id.split('|')[0] : null;
}

function gem_subscription_filter(subs: {data: Array<StripeSubscription>}) {
    return subs.data.find(sub => gem_prefixes.indexOf(planPrefix(sub)) !== -1);
}

class PaymentItem {
    amount_due: number;
    amount_paid: number;
    balance: number;
    card: card;
    date: number;
    description: string;
    id: string;
    paired: string;
    paired_item: PaymentItem;
    type: 'invoice'|'charge';
    constructor() {
    }
}

function invoiceDesc(inv: Invoice) {
    let desc = "";
    inv.lines.data.forEach(function(l: any) {
        if (l.plan && l.plan.id && !isNaN(parseInt(l.plan.id, 10))) { // legacy plans
            desc = l.plan.id + " " + desc;
        } else if (l.plan && l.plan.id.split('|')[0] === 'GEM') {
            desc += 'Gust Equity Management subscription';
        } else if (l.description) {
            desc += "+ " + l.description + " ";
        }
    });
    return desc;
}

function clearCache(obj: Payments) {
    return function<T>(data: T = undefined): Promise<T> {
        obj.__cache = {};
        return Promise.resolve(data);
    };
}

let get_customer_promise: Promise<StripeCustomer>;
let paymentItemsPromise: Promise<Array<PaymentItem>>;
const paymentItems: Array<PaymentItem> = [];
let monthly: boolean;

export class Payments {
    public customer: StripeCustomer;
    private subscription: StripeSubscription;
    public __cache: {
        paymentInputs?: Promise<account.my_payment_inputs>;
        planFeatures?: Promise<account.paid_features>;
    };
    private invoices: invoiceCollection;

    constructor() {
        this.customer = undefined;
        this.subscription = undefined;
        this.__cache = {};
        this.resetCustomerPromise()
            .then(() => this.refreshPaymentItems());
    }

    paymentInputs() {
        if (!this.__cache.paymentInputs) {
            this.__cache.paymentInputs = ferry.selectone<account.my_payment_inputs>('account.my_payment_inputs');
        }
        return this.__cache.paymentInputs;
    }

    planFeatures() {
        if (!this.__cache.planFeatures) {
            this.__cache.planFeatures = ferry.selectone<account.paid_features>('account.paid_features');
        }
        return this.__cache.planFeatures;
    }

    onboardPrice(stakeholders: number) {
        return (Math.ceil(Math.max(1, stakeholders) / 25) * 100) || 0;
    }

    valuationSubscription() {
        return this.customer.subscriptions.data.find(sub => planPrefix(sub) === "409a");
    }

    gemSubscription() {
        return this.customer ? gem_subscription_filter(this.customer.subscriptions) : undefined;
    }

    hasAnySubscription() {
        return this.customer.subscriptions.data.length > 0;
    }

    update_payment(response: card) {
        return ferry.call('/payment/card', { token: response.id }).then((resp: StripeCustomer) => {
            this.customer = resp;
            return Promise.resolve(this.customer);
        }).then(clearCache(this));
    }

    reactivateSub() {
        return ferry.call('/payment/reactivate', {}).then((resp: StripeCustomer) => {
            this.customer = resp;
            return sessionState.refreshCookie().then(() => {
                return this.customer;
            });
        }).then(clearCache(this));
    }

    activateSub(planCategory: string, plan: account.plans, stripe_coupon_id?: string) {
        let queryString = upgradePath(planCategory);
        return ferry.call(queryString, { plan: plan, stripe_coupon_id: stripe_coupon_id}).then((resp: StripeCustomer) => {
            this.customer = resp;
            return Promise.resolve(this.customer);
        }).then(clearCache(this));
    }

    isTrialing() {
        const stillInTrial = this.gemSubscription() && ((this.gemSubscription().trial_end * 1000) > new Date().getTime());
        const noInvoices = !(this.invoices && this.invoices.data.length > 0);
        return stillInTrial && noInvoices;
    }

    charge(charge: number, description: string, coupon: string = undefined, metadata: {[key: string ]: boolean} = {}): Promise<StripeCustomer> {
        return ferry.call('/payment/charge', { charge, description, coupon, metadata }).then((resp: StripeCustomer) => {
            this.customer = resp;
            return this.customer;
        }).then(clearCache(this));
    }

    pay_invoice() {
        return ferry.call('/payment/pay').then(() => {
            this.resetCustomerPromise();
            this.customer = null;
            this.invoices = null;
            return this.refreshPaymentItems();
        });
    }

    resetCustomerPromise() {
        const has_billing = sessionState.rights &&
            (sessionState.rights.billing === 'view' || sessionState.rights.billing === 'edit');
        get_customer_promise = has_billing ? ferry.call('/payment/customer').then((resp: StripeCustomer) => {
                this.customer = resp;
                this.customer.currency = this.customer.currency || 'usd';
                return this.customer;
            }) : Promise.reject(undefined);
        return get_customer_promise;
    }

    get_customer() {
        if (this.customer) {
            return Promise.resolve(this.customer);
        } else {
            return get_customer_promise;
        }
    }

    handleAutoUpgrade() {
        clearCache(this)();
        this.resetCustomerPromise();
        this.customer = null;
    }

    get_subscription() {
        return this.get_customer().then(() => {
            const sub = this.gemSubscription();
            if (sub) {
                const pieces = sub.plan.id.split('|');
                if (pieces[0] === 'GEM') {
                    sub.plan_level = pieces[1];
                } else {
                    sub.plan_level = pieces[0];
                }
            }
            return sub;
        });
    }

    get_invoices(n: number) {
        if (this.invoices && (this.invoices.data.length > n || !this.invoices.has_more)) {
            return Promise.resolve(this.invoices.data.slice(0, Math.min(n, this.invoices.data.length)));
        } else {
            return ferry.call('/payment/invoices', { count: 50 }).then((resp: invoiceCollection) => {
                this.invoices = resp;
                return Promise.resolve(this.invoices.data.slice(0, Math.min(n, this.invoices.data.length)));
            });
        }
    }

    get_invoice(id: string): Promise<Invoice> {
        return ferry.call('/payment/invoice/' + id);
    }
    get_charges(n: number): Promise<chargeCollection> {
        return ferry.call('/payment/charges', { count: n });
    }
    get_charge(id: string): Promise<charge> {
        return ferry.call('/payment/charge/' + id);
    }
    get_upcoming_invoice() {
        return this.get_subscription().then((sub) => {
            // stripe throws an error if we try to retrieve a non-existant upcoming invoice
            // so we check for subscriptions first, and if none exist, resolve undefined
            if (sub && !sub.cancel_at_period_end) {
                return <Promise<Invoice>>ferry.call('/payment/upcoming_invoice/');
            } else {
                return <Promise<Invoice>>Promise.resolve(undefined);
            }
        });
    }
    format_discount() {
        // coupon applied to subscription takes priority over one applied to customer
        let discount: discount;
        if (this.gemSubscription() && this.gemSubscription().discount) {
            discount = this.gemSubscription().discount;
        } else {
            discount = this.customer.discount;
        }

        if (!discount) {
            return;
        }
        const cpn = discount.coupon;
        let formatted_coupon: string;
        if (cpn.percent_off) {
            formatted_coupon = cpn.percent_off + "% off";
        } else {
            formatted_coupon = '$' + String(cpn.amount_off / 100) + " off";
        }
        if (discount.end) {
            const d = moment.utc(discount.end * 1000).format('MMMM D, YYYY');
            formatted_coupon += ' until ' + d;
        }
        return formatted_coupon;
    }

    isMonthly() {
        return monthly;
    }
    private refreshPaymentItems() {
        paymentItems.splice(0);

        function merge(items: Array<PaymentItem>) {
            monthly = true;
            items.forEach((item) => {
                if (item.paired) {
                    const matches = items.filter(i => i.id === item.paired);
                    if (matches.length > 0) { // shouldn't be possible to be > 1
                        item.paired_item = matches[0];
                        if (!item.balance && item.paired_item.balance) {
                            item.balance = item.paired_item.balance;
                        }
                    }
                } else {
                    if (item.type === 'charge') {
                        monthly = false; // monthly implies that every charge is attached to an invoice
                    }
                }
            });
        }

        function propagate_balance(items: Array<PaymentItem>, balance: number) { // assumes items are sorted in desc chronological order
            items.forEach((item) => {
                if (!item.balance) {
                    item.balance = balance;
                } else {
                    balance = Number(item.balance);
                }
                balance -= Number(item.amount_due);
            });
        }

        function paymentSort(a: PaymentItem, b: PaymentItem) {
            return b.date - a.date;
        }

        const pinv = this.get_invoices(50).then((invoices: Array<Invoice>) => {
            invoices.forEach((inv) => {
                if (!inv.paid) { // Either the company is zombied, or (more likely) this is the
                    return; // three hour window between when the invoice is created and the charge is attempted.
                }
                const p = new PaymentItem();
                p.date = inv.date;
                p.type = 'invoice';
                p.id = inv.id;
                p.paired = inv.charge;
                p.amount_due = -1 * inv.total;
                p.balance = -1 * inv.ending_balance;
                p.description = invoiceDesc(inv);

                paymentItems.push(p);
            });
            return Promise.resolve(invoices);
        });

        const pch = this.get_charges(50).then((charges) => {
            charges.data.forEach(ch => {
                if (!ch.paid) {
                    return;
                }
                const p = new PaymentItem();
                p.date = ch.created;
                p.type = 'charge';
                p.card = ch.source;
                p.id = ch.id;
                p.paired = ch.invoice;
                p.amount_paid = ch.amount - ch.amount_refunded;
                p.amount_due = 0;
                if (p.paired) {// Connected to an invoice, which will debit the same amount
                    p.amount_due = ch.amount;
                }
                p.description = 'Payment';
                if (ch.metadata) {
                    if (ch.metadata.credit) {
                        p.amount_due = ch.metadata.credit;
                    }
                    if (ch.metadata.description) {
                        p.description = ch.metadata.description;
                    }
                    if (ch.metadata.account_balance) {
                        p.balance = -1 * ch.metadata.account_balance;
                    }
                }
                if (p.card && p.card.last4) {
                    p.description += ' (Card ending ' + String(p.card.last4) + ')';
                }

                paymentItems.push(p);
            });
            return Promise.resolve(charges);
        });

        return Promise.all([pinv, pch, this.get_customer()]).then(function(res) {
            const customer = res[2];
            merge(paymentItems);
            paymentItems.sort(paymentSort);
            propagate_balance(paymentItems, -1 * Number(customer.account_balance));
            return Promise.resolve(paymentItems);
        });
    }

    getPaymentItems() {
        if (!paymentItemsPromise) {
            paymentItemsPromise = this.refreshPaymentItems().then((pItems: PaymentItem[]) => pItems);
        }
        return paymentItemsPromise;
    }
}

export const payments = new Payments();



// WEBPACK FOOTER //
// ./_src/services/payments/payments.ts